<script lang="ts" setup>
import type { FavoriteList } from '~/models/Favorites'
import type { IsValid, ValidateFormResults } from '~/composables/useValidation'
import { useI18n } from 'vue-i18n'
import { reactive, watch } from 'vue'
import { PendoFeature } from '~/models/Pendo'
import { useValidation } from '~/composables/useValidation'
import Button from '~/components/ui/Button.vue'

interface FormData {
  name: string
  note: string
}

const props = defineProps<{
  item: FavoriteList
  isLoading: boolean
}>()

const emit = defineEmits<{
  close: []
  update: [item: FavoriteList]
}>()

const { t } = useI18n()
const { rules, validateForm } = useValidation()

const form = reactive<FormData>({ name: props.item.name ?? '', note: props.item.description ?? '' })
const validationResults = reactive<ValidateFormResults<FormData> & IsValid>({
  name: [],
  note: [],
  isValid: false,
})

const update = () => {
  if (!validationResults.isValid)
    return
  emit('update', { ...props.item, name: form.name, description: form.note })
}

watch(form, () => validateForm(
  form,
  {
    name: [
      { rule: rules.string(), message: t('validation.favorite.name.error') },
      { rule: rules.required(), message: t('validation.general.required.error') },
    ],
    note: [
      { rule: rules.string(), message: t('validation.favorite.note.error') },
    ],
  },
  validationResults,
))
</script>

<template>
  <div class="flex flex-col gap-6">
    <label class="space-y-2">
      <p
        class="font-semi-bold text-black"
        v-text="t('favorite.editListNameLabel')"
      />
      <input
        v-model="form.name"
        class="border-gray-10 placeholder:text-gray-40 w-full rounded-xl border bg-white px-4 py-2 text-base transition focus-visible:ring"
        type="text"
        @keydown.enter.stop.prevent="update"
      >
    </label>
    <ul
      v-if="validationResults.name.some(r => r.isError)"
      class="text-coral-40"
    >
      <li
        v-for="(error, i) in validationResults.name"
        :key="`error-${i}`"
        v-text="error.message"
      />
    </ul>
    <label class="space-y-2">
      <p
        class="text-black"
        v-text="t('favorite.editListNoteLabel')"
      />
      <textarea
        v-model="form.note"
        class="border-gray-10 placeholder:text-gray-40 w-full rounded-xl border bg-white px-4 py-2 text-base transition focus-visible:ring"
        :placeholder="$t('media.writeNote')"
        @keydown.enter.stop.prevent="update"
      />
    </label>
    <div class="flex justify-end gap-2">
      <Button
        :data-pendo="PendoFeature.Favorites.ChangeName"
        class="justify-self-end"
        :disabled="!validationResults.isValid || isLoading"
        size="lg"
        @click="update"
      >
        {{ t('save') }}
      </Button>
    </div>
  </div>
</template>
